import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { getAuthUser } from '@rd/core/auth/store/selectors/auth.selectors';
import { IconRegistryService } from '@rd/core/services/icon-registry/icon-registry.service';
import { DialogConfig, DialogScreenMode } from '@rd/popup/dialog-config';
import { DialogRef } from '@rd/popup/dialog-ref';
import {
  LoyaltyProgram,
  MembershipType,
  PointsTransferMembership,
  User,
} from '@rd/types';
import { Observable, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { TransferConnectV2SettingService } from '../../services/transfer-connect-v2-setting/transfer-connect-v2-setting.service';
import {
  CreateMembership,
  UpdateMembership,
} from '../../store/actions/membership.actions';
import { TransferConnectV2State } from '../../store/reducers';
import { getCreateOrUpdateMembershipIsLoading } from '../../store/selectors/membership.selectors';
import { getLoyaltyProgramMembership } from '../../store/selectors/transfer-connect-combined.selectors';
import { TransferConnectV2MembershipDetailFormData } from '../../types';

@Component({
  selector: 'rd-tcv2-checkout-link-account-dialog',
  templateUrl: './checkout-link-account-dialog.component.html',
  styleUrls: ['./checkout-link-account-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TcV2CheckoutLinkAccountDialogComponent
  implements OnInit, OnDestroy
{
  @HostBinding('class.rd-tcv2-checkout-link-account-dialog')
  componentClass = true;
  private destroy$ = new Subject();

  createOrUpdateMembershipIsLoading$: Observable<boolean>;

  loyaltyProgram: LoyaltyProgram;
  loyaltyProgramMembership: PointsTransferMembership | null;
  screenMode: DialogScreenMode;

  isSubmitted$: Subject<boolean> = new Subject();
  shouldDisablePrefilledLinkAccountFields: boolean;
  shouldPrefillLinkAccountForm: boolean;
  user: User;

  constructor(
    private store: Store<TransferConnectV2State>,
    private dialogRef: DialogRef,
    private dialogConfig: DialogConfig<{
      loyaltyProgram: LoyaltyProgram;
      screenMode: DialogScreenMode;
    }>,
    private iconRegistryService: IconRegistryService,
    private transferConnectV2SettingService: TransferConnectV2SettingService,
  ) {
    // @ts-ignore
    this.loyaltyProgram = this.dialogConfig.data.loyaltyProgram;
    // @ts-ignore
    this.screenMode = this.dialogConfig.data.screenMode;
    this.registerIcons();
  }

  ngOnInit(): void {
    this.store
      .select(getLoyaltyProgramMembership, { id: this.loyaltyProgram.id })
      .pipe(takeUntil(this.destroy$))
      .subscribe((loyaltyProgramMembership) => {
        this.loyaltyProgramMembership = loyaltyProgramMembership;
      });

    this.createOrUpdateMembershipIsLoading$ = this.store.select(
      getCreateOrUpdateMembershipIsLoading,
    );
    this.shouldPrefillLinkAccountForm =
      this.transferConnectV2SettingService.shouldPrefillLinkAccountForm;
    this.shouldDisablePrefilledLinkAccountFields =
      this.transferConnectV2SettingService.shouldDisablePrefilledLinkAccountFields;

    if (this.shouldPrefillLinkAccountForm) {
      this.store
        .select(getAuthUser)
        .pipe(take(1))
        .subscribe((authUser) => {
          // @ts-ignore
          this.user = authUser;
        });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
  }

  linkAnAccount(formData: TransferConnectV2MembershipDetailFormData): void {
    if (this.loyaltyProgramMembership && this.loyaltyProgramMembership.id) {
      this.store.dispatch(
        new UpdateMembership({
          updateMembershipData: {
            type: MembershipType.PointsTransfer,
            membershipId: this.loyaltyProgramMembership.id,
            loyaltyProgramShortName: this.loyaltyProgram.shortName,
            membership: {
              number: formData.membershipNumber,
              firstName: formData.firstName,
              lastName: formData.lastName,
              details: {
                dateOfBirth: formData.dateOfBirth,
              },
            },
          },
        }),
      );
    } else {
      this.store.dispatch(
        new CreateMembership({
          type: MembershipType.PointsTransfer,
          loyaltyProgramShortName: this.loyaltyProgram.shortName,
          membership: {
            loyalty_program_id: this.loyaltyProgram.id,
            number: formData.membershipNumber,
            firstName: formData.firstName,
            lastName: formData.lastName,
            details: {
              dateOfBirth: formData.dateOfBirth,
            },
          },
        }),
      );
    }
  }

  membershipDetailFormData(): TransferConnectV2MembershipDetailFormData | null {
    if (!!this.loyaltyProgramMembership) {
      // eslint-disable-next-line @typescript-eslint/naming-convention, no-underscore-dangle, id-blacklist, id-match
      const { firstName, lastName, number, dateOfBirth } =
        this.loyaltyProgramMembership;

      // @ts-ignore
      return { firstName, lastName, membershipNumber: number, dateOfBirth };
    } else {
      return null;
    }
  }

  dismissLinkAccountDialog(): void {
    this.dialogRef.close();
  }

  get closeIcon(): string {
    if (this.screenMode === 'fullscreen') {
      return this.transferConnectV2SettingService
        .linkAccountDialogMobileCloseIcon;
    } else {
      return 'close';
    }
  }

  private registerIcons(): void {
    this.iconRegistryService.registerIcon(this.closeIcon);
  }
}
